import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Settings } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuAction,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { SETTINGS_CATEGORIES } from "@/constants/settings-categories";
import { useSidebarGroup } from "@/stores";
import { NavMorePopover } from "./nav-more-popover";

/** One category row — same markup inline, in the "+N more" popover and the hover card. */
function CategoryRow({
  category,
  active,
}: {
  category: (typeof SETTINGS_CATEGORIES)[number];
  active: boolean;
}) {
  return (
    <SidebarMenuSubButton asChild isActive={active} size="sm">
      <Link to={`/dashboard/settings/${category.key}`}>
        <span className="truncate">{category.label}</span>
      </Link>
    </SidebarMenuSubButton>
  );
}

export function SettingsNav() {
  const location = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  // Open state + inline row count come from the user's sidebar prefs
  // (gear next to "Main Menu"); the chevron toggle is remembered.
  const { open, setOpen, itemsShown } = useSidebarGroup("settings");

  const isActive = location.pathname.includes("/dashboard/settings");
  const isCategoryActive = (key: string) =>
    location.pathname.includes(`/dashboard/settings/${key}`);

  if (isCollapsed) {
    return (
      <SidebarMenuItem>
        <HoverCard openDelay={0} closeDelay={0}>
          <HoverCardTrigger asChild>
            <SidebarMenuButton isActive={isActive}>
              <Settings />
              <span>Settings</span>
            </SidebarMenuButton>
          </HoverCardTrigger>

          <HoverCardContent
            side="right"
            align="start"
            className="w-56 flex flex-col gap-1 p-2 pt-0.5 rounded-xl max-h-96.5 overflow-auto [scrollbar-color:--alpha(var(--foreground)/30%)_transparent] scrollbar-thin"
            sideOffset={-1}
          >
            <Link
              to="/dashboard/settings"
              className="my-2.5 mx-1 mb-1 text-xs font-medium text-muted-foreground hover:text-foreground group"
            >
              Settings{" "}
              <ChevronRight className="size-3.5 inline group-hover:translate-x-0.5 transition-transform" />
            </Link>

            <div className="border-b border-dashed w-auto" />

            {SETTINGS_CATEGORIES.map((category) => (
              <ScopeWrapper key={category.key} color="primary">
                <CategoryRow
                  category={category}
                  active={isCategoryActive(category.key)}
                />
              </ScopeWrapper>
            ))}
          </HoverCardContent>
        </HoverCard>
      </SidebarMenuItem>
    );
  }

  // itemsShown 0 = the group never expands: no chevron, no sub-list.
  const canExpand = itemsShown > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip="Settings" isActive={isActive}>
          <Link to="/dashboard/settings">
            <Settings />
            <span>Settings</span>
          </Link>
        </SidebarMenuButton>

        {canExpand && (
          <>
            <CollapsibleTrigger asChild>
              <SidebarMenuAction className="data-[state=open]:rotate-90 hover:bg-primary/10 cursor-pointer rounded-xs">
                <ChevronRight className="text-primary/60 group-hover:text-primary" />
                <span className="sr-only">Toggle</span>
              </SidebarMenuAction>
            </CollapsibleTrigger>

            <CollapsibleContent className="w-full mt-0.5">
              <SidebarMenuSub className="w-full pr-4">
                {SETTINGS_CATEGORIES.slice(0, itemsShown).map((category) => (
                  <ScopeWrapper key={category.key} color="primary">
                    <SidebarMenuSubItem>
                      <CategoryRow
                        category={category}
                        active={isCategoryActive(category.key)}
                      />
                    </SidebarMenuSubItem>
                  </ScopeWrapper>
                ))}
              </SidebarMenuSub>

              <NavMorePopover count={SETTINGS_CATEGORIES.length - itemsShown}>
                {SETTINGS_CATEGORIES.slice(itemsShown).map((category) => (
                  <ScopeWrapper key={category.key} color="primary">
                    <CategoryRow
                      category={category}
                      active={isCategoryActive(category.key)}
                    />
                  </ScopeWrapper>
                ))}
              </NavMorePopover>
            </CollapsibleContent>
          </>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
}
